import { FormCard } from "Components/Form/FormCard";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Col, Input, Row, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatMcpTs } from "./mcpAdminFormat";

const ConnectionToolsTable = ({ tools }: { tools: API_McpConnectionToolVisibility[] }) => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const toolColumns: ColumnsType<API_McpConnectionToolVisibility> = [
    { dataIndex: "toolName", title: t("Tool") },
    { dataIndex: "callCount", title: t("Calls (lifetime)"), width: 130 },
    {
      dataIndex: "lastUsed",
      title: t("Last used"),
      width: 220,
      render: (value: string | number) => formatMcpTs(value),
    },
  ];
  return (
    <Table
      rowKey={(tool) => tool.toolName ?? ""}
      columns={toolColumns}
      dataSource={tools}
      pagination={false}
      size="small"
    />
  );
};

export const McpConnectionsTab = () => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<API_McpAdminConnection[]>([]);
  const [userFilter, setUserFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await API.getData<API_McpAdminConnection[]>(API_URL.MCP_ADMIN.getConnections());
        setRows(data ?? []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const userQ = userFilter.trim().toLowerCase();
    const clientQ = clientFilter.trim().toLowerCase();
    return rows.filter((row) => {
      const userHay = `${row.userId ?? ""} ${row.userName ?? ""}`.toLowerCase();
      const clientHay = (row.clientId ?? "").toLowerCase();
      return (!userQ || userHay.includes(userQ)) && (!clientQ || clientHay.includes(clientQ));
    });
  }, [rows, userFilter, clientFilter]);

  const columns: ColumnsType<API_McpAdminConnection> = [
    { dataIndex: "userId", title: t("User ID"), width: 100 },
    { dataIndex: "userName", title: t("User"), ellipsis: true },
    { dataIndex: "clientId", title: t("Client ID"), ellipsis: true },
    {
      dataIndex: "lastUsed",
      title: t("Last used"),
      width: 220,
      render: (value: string | number) => formatMcpTs(value),
    },
  ];

  return (
    <FormCard title={t("Connections")} loading={loading}>
      <p className="mb-2">
        {t(
          "Not open sessions — heartbeats: one row per user and MCP client after a tool has run. " +
            "Rows remain after disconnect. " +
            "Calls are the lifetime total; Last used is only the latest call, " +
            "not the span of the calls."
        )}
      </p>
      <Row gutter={16} className="mb-2">
        <Col xs={24} md={8}>
          <Input
            allowClear
            aria-label={t("Filter user")}
            placeholder={t("Filter user")}
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
          />
        </Col>
        <Col xs={24} md={8}>
          <Input
            allowClear
            aria-label={t("Filter client")}
            placeholder={t("Filter client")}
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          />
        </Col>
      </Row>
      <Table
        rowKey={(row) => `${row.userId}-${row.clientId}`}
        columns={columns}
        dataSource={filtered}
        size="small"
        pagination={false}
        expandable={{
          rowExpandable: (record) => (record.tools?.length ?? 0) > 0,
          expandedRowRender: (record) => <ConnectionToolsTable tools={record.tools ?? []} />,
        }}
      />
    </FormCard>
  );
};
